import { Component, OnChanges, OnInit, computed, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SelectModule } from 'primeng/select';
import { CheckboxModule } from 'primeng/checkbox';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinCanAccessDirective } from '../../../shared/security/access.directive';
import {
  WORKFLOW_SYSTEM_TASK_MODULE_KEYS,
  WorkflowModuleOperationCatalogItem,
  WorkflowServiceOperatorConfig
} from '../workflow-catalog.models';
import { WorkflowApiService } from '../workflow-api.service';
import { parseConfigJson, parseStringMapping, stringifyConfigJson } from './workflow-operator-config.utils';
import { WorkflowVariableMappingGridComponent } from './workflow-variable-mapping-grid.component';
import { WorkflowRetryPolicyFormComponent } from './workflow-retry-policy-form.component';

@Component({
  standalone: true,
  selector: 'ain-workflow-service-operator-panel',
  imports: [
    FormsModule,
    SelectModule,
    CheckboxModule,
    AinCanAccessDirective,
    WorkflowVariableMappingGridComponent,
    WorkflowRetryPolicyFormComponent
  ],
  template: `
    <section class="operator-panel" ainCanAccess [ainCanAccessPermission]="'workflow.operations.catalog.read'">
      <h5>{{ t('workflow.designer.operators.service.title') }}</h5>

      <label>{{ t('workflow.designer.operators.service.moduleKey') }}</label>
      <p-select
        [options]="moduleOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.moduleKey"
        (ngModelChange)="onModuleChanged()"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      <label>{{ t('workflow.designer.operators.service.operationKey') }}</label>
      <p-select
        [options]="operationOptions()"
        optionLabel="label"
        optionValue="value"
        [(ngModel)]="model.operationKey"
        (ngModelChange)="emitConfig()"
        [filter]="true"
        appendTo="body"
        [placeholder]="t('common.select')" />

      <h6>{{ t('workflow.designer.operators.inputMapping') }}</h6>
      <ain-workflow-variable-mapping-grid
        [mapping]="model.inputMapping ?? {}"
        (mappingChange)="onInputMappingChanged($event)" />

      <h6>{{ t('workflow.designer.operators.outputMapping') }}</h6>
      <ain-workflow-variable-mapping-grid
        [mapping]="model.outputMapping ?? {}"
        (mappingChange)="onOutputMappingChanged($event)" />

      <h6>{{ t('workflow.designer.operators.retry.title') }}</h6>
      <ain-workflow-retry-policy-form
        [retryPolicy]="model.retryPolicy"
        (retryPolicyChange)="onRetryPolicyChanged($event)" />

      @if (selectedOperation()?.supportsCompensation) {
        <div class="compensation-block">
          <label>
            <p-checkbox [(ngModel)]="compensationEnabled" (ngModelChange)="onCompensationToggle()" [binary]="true" />
            {{ t('workflow.designer.operators.service.enableCompensation') }}
          </label>

          @if (compensationEnabled) {
            <label>{{ t('workflow.designer.operators.service.compensationOperation') }}</label>
            <p-select
              [options]="compensationOperationOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="model.compensation!.operationKey"
              (ngModelChange)="emitConfig()"
              appendTo="body"
              [placeholder]="t('common.select')" />
          }
        </div>
      }
    </section>
  `,
  styles: [`
    .operator-panel { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; }
    .operator-panel h5, .operator-panel h6 { margin: 8px 0 4px; font-size: 13px; color: #334155; }
    .operator-panel label { font-size: 12px; color: #475569; }
    .compensation-block { display: flex; flex-direction: column; gap: 8px; margin-top: 8px; padding-top: 8px; border-top: 1px solid #e2e8f0; }
  `]
})
export class WorkflowServiceOperatorPanelComponent implements OnInit, OnChanges {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);

  readonly operatorType = input<'Service' | 'SystemTask'>('Service');
  readonly configJson = input('{}');
  readonly configJsonChange = output<string>();

  readonly catalog = signal<WorkflowModuleOperationCatalogItem[]>([]);
  model: WorkflowServiceOperatorConfig = {};
  compensationEnabled = false;

  readonly moduleOptions = computed(() => {
    const items = this.catalog();
    const keys = new Set<string>();
    for (const item of items) {
      if (this.operatorType() === 'SystemTask') {
        if (WORKFLOW_SYSTEM_TASK_MODULE_KEYS.includes(item.moduleKey as typeof WORKFLOW_SYSTEM_TASK_MODULE_KEYS[number])) {
          keys.add(item.moduleKey);
        }
      } else if (!WORKFLOW_SYSTEM_TASK_MODULE_KEYS.includes(item.moduleKey as typeof WORKFLOW_SYSTEM_TASK_MODULE_KEYS[number])) {
        keys.add(item.moduleKey);
      }
    }
    return [...keys].sort().map(value => ({ label: value, value }));
  });

  readonly operationOptions = computed(() => {
    const moduleKey = this.model.moduleKey;
    if (!moduleKey) return [];
    return this.catalog()
      .filter(item => item.moduleKey === moduleKey)
      .map(item => ({ label: `${item.operationKey} — ${item.displayName}`, value: item.operationKey }));
  });

  readonly compensationOperationOptions = computed(() => this.operationOptions());

  ngOnInit(): void {
    this.loadCatalog();
    this.loadFromConfig(this.configJson());
  }

  ngOnChanges(): void {
    this.loadFromConfig(this.configJson());
  }

  selectedOperation(): WorkflowModuleOperationCatalogItem | undefined {
    if (!this.model.moduleKey || !this.model.operationKey) return undefined;
    return this.catalog().find(item =>
      item.moduleKey === this.model.moduleKey && item.operationKey === this.model.operationKey);
  }

  onModuleChanged(): void {
    this.model.operationKey = undefined;
    this.emitConfig();
  }

  onInputMappingChanged(mapping: Record<string, string>): void {
    this.model.inputMapping = mapping;
    this.emitConfig();
  }

  onOutputMappingChanged(mapping: Record<string, string>): void {
    this.model.outputMapping = mapping;
    this.emitConfig();
  }

  onRetryPolicyChanged(retryPolicy: WorkflowServiceOperatorConfig['retryPolicy']): void {
    this.model.retryPolicy = retryPolicy;
    this.emitConfig();
  }

  onCompensationToggle(): void {
    if (!this.compensationEnabled) {
      this.model.compensation = undefined;
    } else {
      this.model.compensation = this.model.compensation ?? { operationKey: this.selectedOperation()?.compensationOperationKey ?? undefined };
    }
    this.emitConfig();
  }

  emitConfig(): void {
    this.configJsonChange.emit(stringifyConfigJson(this.model));
  }

  private loadCatalog(): void {
    this.api.getOperationsCatalog().subscribe({
      next: items => this.catalog.set(items),
      error: () => this.catalog.set([])
    });
  }

  private loadFromConfig(raw: string): void {
    this.model = parseConfigJson<WorkflowServiceOperatorConfig>(raw, {
      moduleKey: '',
      operationKey: '',
      inputMapping: {},
      outputMapping: {}
    });
    this.model.inputMapping = parseStringMapping(this.model.inputMapping);
    this.model.outputMapping = parseStringMapping(this.model.outputMapping);
    this.compensationEnabled = !!this.model.compensation?.operationKey;
    if (this.compensationEnabled && !this.model.compensation) {
      this.model.compensation = {};
    }
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
